// src/Layouts/GateEntry/GateEntryDashboard.tsx
//
// Daily gate entry / exit — no forms, no passes. Two tabs:
//
// MARK ENTRIES
//   • Quick scan (left) — scan an ID card (a USB/Bluetooth scanner types the
//     SP ID + Enter) or start typing a name: matches appear as you type, each
//     with one-tap IN / OUT. Enter records the exact SP-ID match (or the only
//     match) using the Auto / Entry / Exit mode.
//     POST /api/movements  { direction: AUTO | IN | OUT }
//
//     Auto-mark ON: no search at all — Enter sends the scanned SP ID straight
//     to POST /api/movements { sp_student_id, direction } (one request).
//     The last 10 marks are kept on screen; scanning one of them again in a
//     way that can't succeed (already IN, already left) is stopped here
//     without calling the server.
//
//   • Right panel —
//       "Marked today" (default): only students marked today, newest first,
//       straight from /movements/live. Each new mark lands at the top with a
//       flash — no point listing hundreds of students nobody has scanned yet.
//       A class chip: that whole class (incl. who hasn't arrived), for bulk
//       IN in the morning / OUT at dismissal. POST /api/movements/bulk, sent
//       in chunks of 200 (the backend's limit).
//
// TODAY'S LOG — everyone recorded, school-wide (parts/TodayGateLog.tsx).
//
// A school day is ONE entry and ONE final exit per student. Short-leave trips
// are not shown here at all (they live on Gate Passes; the backend leaves
// them out of /movements/live and of the log with gate_only=1). So each row
// shows a single next action — Mark IN (not arrived), Mark OUT
// (inside) — or "Done for the day" after the final exit. The backend enforces
// the same rule (late, dismissal vs. unauthorized, pass linking, rejecting
// anything after the final exit); rejections show as toasts.
//
// Live status: GET /api/movements/live (each student's latest movement today),
// patched locally the moment this desk records something. Marks from other
// gates/desks arrive over the gate socket (parts/useGateSocket.ts) — while it's
// connected there is no polling; if it's down, the old 30 s refresh takes over.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import useClassesStore from '../../stateManagers/store/useClassesStore';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import { formatTime, toDateParam } from '../GatePasses/parts/types';
import TodayGateLog, { LogTypeFilter } from './parts/TodayGateLog';
import { SocketMovement, useGateSocket } from './parts/useGateSocket';
import ProfilePictureHover from '../../components/ProfilePictureHover';

// ── Types ───────────────────────────────────────────────────────────────────

type Direction = 'IN' | 'OUT';
type ScanMode = 'AUTO' | Direction;
type LiveStatus = 'in_campus' | 'left_for_day' | 'departed_early' | 'out_on_pass' | 'out_unauthorized';
// came_in = entered today (whether or not they've left since);
// inside = still inside right now; out = out right now (left / short leave).
type RosterFilter = 'all' | 'not_arrived' | 'came_in' | 'inside' | 'out';

interface LiveRow {
  student_id: string;
  name: string;
  sp_student_id: string;
  last_direction: Direction;
  last_type: string;
  last_movement_at: string;
  status: LiveStatus;
  overdue: boolean;
  first_in_at?: string | null; // the day's entry time
  last_out_at?: string | null; // the most recent exit time
  arrived_late?: boolean;
  class_id?: string;
  class_name?: string;
  section?: string;
  roll_no?: number;
  photo_url?: string;
}

interface RosterStudent {
  studentId: string;
  name: string;
  spStudentId: string;
  rollNo: number | null;
  photoUrl: string;
  classId: string;
  className: string;
  section: string;
  classLabel: string; // "8th-A"
}

interface RecordedMovement {
  movement_id?: string; // dedupes our own marks when the socket echoes them back
  student_id: string;
  student_name?: string;
  direction: Direction;
  type: string;
  timestamp: string;
}

// ── Display helpers ─────────────────────────────────────────────────────────

const ALL_CLASSES = '__all__';
const BULK_CHUNK = 200; // POST /movements/bulk accepts at most 200 ids
const FLASH_MS = 2200;

const IN_COLOR = '#16a34a';
const OUT_COLOR = '#334155';

const STATUS_DISPLAY: Record<LiveStatus | 'not_arrived', { label: string; color: string; bg: string; icon: string }> = {
  not_arrived: { label: 'Not arrived', color: '#94a3b8', bg: 'rgba(148,163,184,.12)', icon: 'pi pi-minus-circle' },
  in_campus: { label: 'Inside', color: '#15803d', bg: 'rgba(22,163,74,.10)', icon: 'pi pi-check-circle' },
  left_for_day: { label: 'Left', color: '#475569', bg: 'rgba(100,116,139,.10)', icon: 'pi pi-sign-out' },
  departed_early: { label: 'Gone home (pass)', color: '#c2410c', bg: 'rgba(249,115,22,.10)', icon: 'pi pi-home' },
  out_on_pass: { label: 'Out on short leave', color: '#0f766e', bg: 'rgba(20,184,166,.10)', icon: 'pi pi-replay' },
  out_unauthorized: { label: 'Left without pass', color: '#b91c1c', bg: 'rgba(239,68,68,.10)', icon: 'pi pi-exclamation-triangle' },
};

const TYPE_LABEL: Record<string, string> = {
  regular: 'on time',
  late: 'late',
  dismissal: 'dismissal',
  short_leave: 'short leave',
  return: 'back from short leave',
  early_departure: 'early departure',
  unauthorized: 'without a pass',
};

// Same mapping as gateLiveStatus in gateController.go, so a local update
// looks exactly like what the next /movements/live refresh will return.
const statusFromMovement = (direction: Direction, type: string): LiveStatus => {
  if (direction === 'IN') return 'in_campus';
  if (type === 'short_leave') return 'out_on_pass';
  if (type === 'early_departure') return 'departed_early';
  if (type === 'unauthorized') return 'out_unauthorized';
  return 'left_for_day';
};

// What can happen next for a student, given today's status. null = the day
// is over (dismissal / early departure / left without pass).
const nextActionFor = (status: LiveStatus | 'not_arrived'): Direction | null => {
  if (status === 'in_campus') return 'OUT';
  if (status === 'not_arrived' || status === 'out_on_pass') return 'IN';
  return null;
};

const GATES = [
  { value: 'main', label: 'Main gate' },
  { value: 'back', label: 'Back gate' },
];

const GATE_STORAGE_KEY = 'gate-entry:gate';
const AUTO_MARK_STORAGE_KEY = 'gate-entry:auto-mark';
const RECENT_LIMIT = 10;

// One of the last 10 marks made by scanning (newest first). Used to stop
// repeat scans locally instead of asking the server again.
interface RecentScan {
  sp: string;
  name: string;
  direction: Direction;
  at: string;
  repeat?: boolean; // the server said "already …" — nothing new was recorded
}

const readStoredAutoMark = (): boolean => {
  try {
    return localStorage.getItem(AUTO_MARK_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

// Why a scan of a recently marked student can't succeed, or null if it can.
// A gate OUT is always the day's final exit, so only "inside → Exit" is new.
const repeatScanReason = (hit: RecentScan, mode: ScanMode): string | null => {
  const when = formatTime(hit.at);
  if (hit.direction === 'IN') {
    if (mode === 'OUT') return null;
    return mode === 'IN'
      ? `${hit.name} is already IN (since ${when}).`
      : `${hit.name} was just marked IN at ${when}. Switch to Exit to mark them out.`;
  }
  return `${hit.name} already left for the day at ${when}.`;
};

const readStoredGate = (): string => {
  try {
    const v = localStorage.getItem(GATE_STORAGE_KEY);
    return v && GATES.some((g) => g.value === v) ? v : 'main';
  } catch {
    return 'main';
  }
};

// Same mapping as StudentsListDashboard: student_id is Student._id (what
// movements key off); _id on these rows is the StudentSession id.
const mapRosterRow = (row: any): RosterStudent => ({
  studentId: row.student_id ?? row.id,
  name: row.name || '',
  spStudentId: row.sp_student_id || '',
  rollNo: typeof row.roll_no === 'number' ? row.roll_no : null,
  photoUrl: row.profile_picture?.url || '',
  classId: row.class_id || '',
  className: row.class_name || '',
  section: row.section || '',
  classLabel: row.class_name ? `${row.class_name}${row.section ? `-${row.section}` : ''}` : '',
});

const liveToRosterStudent = (r: LiveRow): RosterStudent => ({
  studentId: r.student_id,
  name: r.name || '',
  spStudentId: r.sp_student_id || '',
  rollNo: typeof r.roll_no === 'number' ? r.roll_no : null,
  photoUrl: r.photo_url || '',
  classId: r.class_id || '',
  className: r.class_name || '',
  section: r.section || '',
  classLabel: r.class_name ? `${r.class_name}${r.section ? `-${r.section}` : ''}` : '',
});

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// ── Component ───────────────────────────────────────────────────────────────

const GateEntryDashboard: React.FC = () => {
  const toast = useRef<Toast>(null);
  // GlobalInputField doesn't forward a ref, so the scan box is reached through
  // its wrapper: keys bubble up to the wrapper, focus goes via querySelector.
  const scanBoxRef = useRef<HTMLDivElement>(null);
  const scanInput = () => scanBoxRef.current?.querySelector('input') ?? null;
  const scanDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scanSeqRef = useRef(0);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const flashTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // When each student was last patched locally — a live refresh that started
  // before that moment must not overwrite the newer local status.
  const localPatchAt = useRef<Record<string, number>>({});
  // Everything we know about students seen on this screen (roster rows, scan
  // results) — fills class/photo for a brand-new local "Marked today" entry
  // until the next live refresh brings the server's version.
  const knownStudents = useRef<Record<string, RosterStudent>>({});
  const seenMovements = useRef<Set<string>>(new Set());
  const remember = (list: RosterStudent[]) =>
    list.forEach((st) => {
      knownStudents.current[st.studentId] = st;
    });

  const schoolDetails = useSchoolDetailsStore((state) => state.schoolDetails);
  const currentSessionId = schoolDetails?.current_session?.id || null;

  const [tab, setTab] = useState<'mark' | 'log'>('mark');
  const [logRefreshKey, setLogRefreshKey] = useState(0);
  const [logTypeFilter, setLogTypeFilter] = useState<LogTypeFilter>(null);

  // Live status
  const [live, setLive] = useState<Record<string, LiveRow>>({});
  const [liveLoading, setLiveLoading] = useState(false);

  // Scan desk
  const [gate, setGate] = useState<string>(readStoredGate);
  const [scanMode, setScanMode] = useState<ScanMode>('AUTO');
  const [scanText, setScanText] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState<RosterStudent[]>([]);
  const [searchingLive, setSearchingLive] = useState(false);
  const [autoMark, setAutoMark] = useState<boolean>(readStoredAutoMark);
  const [recent, setRecent] = useState<RecentScan[]>([]);
  const [recentFlash, setRecentFlash] = useState<string | null>(null);
  const recentDay = useRef(toDateParam(new Date()));
  // Mirror of `recent` for the scan queue — its loop outlives a render.
  const recentRef = useRef<RecentScan[]>([]);
  // Auto-mark scans waiting for the server, oldest first.
  const scanQueue = useRef<{ sp: string; mode: ScanMode; gate: string }[]>([]);
  const queueRunning = useRef(false);
  const [queued, setQueued] = useState(0);

  // Roster — classes from the shared store (cached across screens)
  const { classOptions, fetchClasses, isFetching: classesLoading, hasFetched: classesFetched } = useClassesStore();
  const [classId, setClassId] = useState<string>(ALL_CLASSES);
  const [section, setSection] = useState<string | null>(null);
  const [roster, setRoster] = useState<RosterStudent[]>([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterSearch, setRosterSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rosterFilter, setRosterFilter] = useState<RosterFilter>('all');
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<Direction | null>(null);
  const [flash, setFlash] = useState<Record<string, Direction>>({});
  // Order of the "Marked today" list. A student is added at the top the first
  // time they're marked and then keeps their place — rows never jump under
  // the mouse when someone is marked again.
  const [markedOrder, setMarkedOrder] = useState<string[]>([]);

  const isAllClasses = classId === ALL_CLASSES;
  const selectedClass = isAllClasses ? null : classOptions.find((c) => c.id === classId) || null;
  const sections: string[] = selectedClass?.sections || [];

  // ── Data loading ───────────────────────────────────────────────────────────

  const fetchLive = useCallback(async () => {
    setLiveLoading(true);
    const startedAt = Date.now();
    try {
      // Stats (inside / late / gone) are counted from these rows, so this is
      // the only request.
      const liveRes = await apiClient.get('api/movements/live');
      if (liveRes.data.status === 'success') {
        const map: Record<string, LiveRow> = {};
        (liveRes.data.data || []).forEach((r: LiveRow) => {
          map[r.student_id] = r;
        });
        setLive((prev) => {
          Object.entries(localPatchAt.current).forEach(([id, at]) => {
            if (at > startedAt && prev[id]) map[id] = prev[id]; // local is newer
            else delete localPatchAt.current[id]; // server has caught up
          });
          return map;
        });
        // Server rows come newest first: anyone new (e.g. marked at another
        // gate) goes on top, everyone already listed keeps their place.
        const serverIds: string[] = (liveRes.data.data || []).map((r: LiveRow) => r.student_id);
        setMarkedOrder((prev) => {
          const listed = new Set(prev);
          const fresh = serverIds.filter((id) => !listed.has(id));
          const kept = prev.filter((id) => map[id] || localPatchAt.current[id]);
          return fresh.length || kept.length !== prev.length ? [...fresh, ...kept] : prev;
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Live status not loaded',
        detail: err.response?.data?.message || 'Could not reach the gate log.',
        life: 4000,
      });
    } finally {
      setLiveLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLive();
  }, [fetchLive]);

  useEffect(() => {
    if (!classesFetched && !classesLoading) fetchClasses(false);
  }, [classesFetched, classesLoading, fetchClasses]);

  // Ready to scan as soon as the page opens.
  useEffect(() => {
    scanInput()?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      Object.values(flashTimers.current).forEach(clearTimeout);
      if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);
    },
    [],
  );

  // Loads every page of the selected class (or the whole school) — page 1
  // first for the total, the rest in parallel, same as the students export.
  const fetchRoster = useCallback(async () => {
    // "Marked today" is built from live status — no directory load needed.
    if (!currentSessionId || isAllClasses) {
      setRoster([]);
      setSelected(new Set());
      return;
    }
    setRosterLoading(true);
    try {
      const base: any = { session_id: currentSessionId, limit: 100, class_id: classId };
      if (section) base.section = section;
      const first = await apiClient.get('/students', { params: { ...base, page: 1 } });
      if (first.data.status !== 'success') throw new Error(first.data.message || 'Failed to load students');

      let rows: any[] = first.data.data || [];
      const totalPages = Math.min(Number(first.data.pagination?.total_pages ?? 1), 50);
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            apiClient.get('/students', { params: { ...base, page: i + 2 } }),
          ),
        );
        rest.forEach((r) => {
          if (r.data.status === 'success') rows = rows.concat(r.data.data || []);
        });
      }

      // DEBUG (remove after): does /students send sp_student_id?
      console.log('[GateEntry] /students rows:', rows.length, '| first row:', rows[0], '| sp_student_id:', rows[0]?.sp_student_id);
      const mapped = rows.map(mapRosterRow);
      remember(mapped);
      setRoster(mapped);
      setSelected(new Set());
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Students not loaded',
        detail: err.response?.data?.message || err.message || 'Could not load the roster.',
        life: 4000,
      });
    } finally {
      setRosterLoading(false);
    }
  }, [classId, section, currentSessionId, isAllClasses]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  // ── Status helpers ─────────────────────────────────────────────────────────

  const statusOf = (studentId: string): LiveStatus | 'not_arrived' => live[studentId]?.status ?? 'not_arrived';
  const nextActionOf = (studentId: string) => nextActionFor(statusOf(studentId));

  // "Marked today": everyone with a movement today, in first-marked order.
  const markedToday = useMemo(
    () => markedOrder.filter((id) => live[id]).map((id) => liveToRosterStudent(live[id])),
    [markedOrder, live],
  );

  const baseList = isAllClasses ? markedToday : roster;

  const filteredRoster = useMemo(() => {
    const q = rosterSearch.trim().toLowerCase();
    return baseList.filter((s) => {
      if (q) {
        const hit =
          s.name.toLowerCase().includes(q) ||
          s.spStudentId.toLowerCase().includes(q) ||
          (s.rollNo !== null && String(s.rollNo) === q);
        if (!hit) return false;
      }
      const st = statusOf(s.studentId);
      if (rosterFilter === 'not_arrived') return st === 'not_arrived';
      if (rosterFilter === 'came_in') return !!live[s.studentId]?.first_in_at;
      if (rosterFilter === 'inside') return st === 'in_campus';
      if (rosterFilter === 'out') return st !== 'not_arrived' && st !== 'in_campus';
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseList, rosterFilter, rosterSearch, live]);

  const rosterCounts = useMemo(() => {
    let inside = 0;
    let notArrived = 0;
    let cameIn = 0;
    baseList.forEach((s) => {
      const st = statusOf(s.studentId);
      if (st === 'in_campus') inside++;
      else if (st === 'not_arrived') notArrived++;
      if (live[s.studentId]?.first_in_at) cameIn++;
    });
    return { inside, notArrived, cameIn, out: baseList.length - inside - notArrived };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseList, live]);

  // ── Real-time roster updates ───────────────────────────────────────────────

  // Patch the live map the moment this desk records something, so the roster
  // row changes instantly instead of waiting for the next refresh.
  const applyLocal = (incoming: RecordedMovement[]) => {
    // Skip what's already applied — our own marks come back over the socket too.
    const movements = incoming.filter((m) => !m.movement_id || !seenMovements.current.has(m.movement_id));
    movements.forEach((m) => m.movement_id && seenMovements.current.add(m.movement_id));
    if (movements.length === 0) return;
    const now = Date.now();
    movements.forEach((m) => {
      localPatchAt.current[m.student_id] = now;
    });
    setMarkedOrder((prev) => {
      const listed = new Set(prev);
      const added = movements.map((m) => m.student_id).filter((id) => !listed.has(id));
      return added.length ? [...added, ...prev] : prev;
    });
    setLive((prev) => {
      const next = { ...prev };
      movements.forEach((m) => {
        const known = knownStudents.current[m.student_id];
        const old = prev[m.student_id];
        next[m.student_id] = {
          ...old,
          class_id: old?.class_id || known?.classId,
          class_name: old?.class_name || known?.className,
          section: old?.section || known?.section,
          roll_no: old?.roll_no ?? known?.rollNo ?? undefined,
          photo_url: old?.photo_url || known?.photoUrl,
          student_id: m.student_id,
          name: m.student_name || old?.name || known?.name || '',
          sp_student_id: old?.sp_student_id || known?.spStudentId || '',
          last_direction: m.direction,
          last_type: m.type,
          last_movement_at: m.timestamp,
          first_in_at: old?.first_in_at || (m.direction === 'IN' ? m.timestamp : null),
          last_out_at: m.direction === 'OUT' ? m.timestamp : old?.last_out_at ?? null,
          arrived_late: old?.arrived_late || m.type === 'late',
          status: statusFromMovement(m.direction, m.type),
          overdue: false,
        };
      });
      return next;
    });
    setFlash((prev) => {
      const next = { ...prev };
      movements.forEach((m) => {
        next[m.student_id] = m.direction;
        clearTimeout(flashTimers.current[m.student_id]);
        flashTimers.current[m.student_id] = setTimeout(() => {
          setFlash((p) => {
            const n = { ...p };
            delete n[m.student_id];
            return n;
          });
        }, FLASH_MS);
      });
      return next;
    });
    setLogRefreshKey((k) => k + 1);
  };

  const scrollToRow = (studentId: string) => {
    // Wait a frame so a filter change (e.g. leaving "Not arrived") has rendered.
    requestAnimationFrame(() => {
      rowRefs.current[studentId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  // ── Live socket ────────────────────────────────────────────────────────────

  // Marks from every gate / desk of this school, pushed as they happen.
  const handleSocketMovements = (rows: SocketMovement[]) => {
    const today = toDateParam(new Date());
    const fresh = rows.filter((r) => r.date === today);
    if (fresh.length === 0) return;
    remember(
      fresh.map((r) => ({
        studentId: r.student_id,
        name: r.name,
        spStudentId: r.sp_student_id,
        rollNo: r.roll_no,
        photoUrl: knownStudents.current[r.student_id]?.photoUrl || '',
        classId: r.class_id || '',
        className: r.class_name,
        section: r.section,
        classLabel: r.class_name ? `${r.class_name}${r.section ? `-${r.section}` : ''}` : '',
      })),
    );
    applyLocal(
      fresh.map((r) => ({
        movement_id: r.movement_id,
        student_id: r.student_id,
        student_name: r.name,
        direction: r.direction,
        type: r.type,
        timestamp: r.timestamp,
      })),
    );
  };

  const socketStatus = useGateSocket(handleSocketMovements, fetchLive);
  const socketLive = socketStatus === 'live';

  // Without the socket, fall back to refreshing every 30 s.
  useEffect(() => {
    if (socketLive) return;
    const t = setInterval(fetchLive, 30_000);
    return () => clearInterval(t);
  }, [socketLive, fetchLive]);

  // After this desk marks someone the row is already patched locally; only
  // ask the server again when the socket isn't keeping us in sync.
  const syncIfOffline = () => {
    if (!socketLive) fetchLive();
  };

  // ── Recording ──────────────────────────────────────────────────────────────

  const recordOne = async (
    student: { studentId: string; name: string },
    direction: ScanMode,
  ): Promise<{ ok: boolean; message: string; movement?: RecordedMovement }> => {
    try {
      const res = await apiClient.post('api/movements', {
        student_id: student.studentId,
        direction,
        gate,
        method: 'manual',
      });
      if (res.data.status === 'success') {
        const movement: RecordedMovement = {
          movement_id: res.data.movement_id,
          student_id: res.data.student_id || student.studentId,
          student_name: res.data.student_name || student.name,
          direction: res.data.direction,
          type: res.data.type,
          timestamp: res.data.timestamp || new Date().toISOString(),
        };
        applyLocal([movement]);
        // Keep the recent-scan list true if this student is in it.
        const sp = (knownStudents.current[movement.student_id]?.spStudentId || '').toUpperCase();
        if (sp && recentRef.current.some((r) => r.sp === sp)) {
          pushRecent({ sp, name: movement.student_name || '', direction: movement.direction, at: movement.timestamp });
        }
        if (movement.type === 'unauthorized') {
          toast.current?.show({
            severity: 'warn',
            summary: 'Left without a pass',
            detail: `${movement.student_name} went out during school hours without an approved gate pass.`,
            life: 7000,
          });
        }
        return { ok: true, message: '', movement };
      }
      return { ok: false, message: res.data.message || 'Could not record this movement.' };
    } catch (err: any) {
      return { ok: false, message: err.response?.data?.message || 'Could not record this movement.' };
    }
  };

  // Search as you type. A sequence number drops stale responses, so fast
  // typing never shows results for an older query.
  const searchStudents = async (q: string): Promise<RosterStudent[] | null> => {
    if (!currentSessionId) return [];
    const seq = ++scanSeqRef.current;
    setSearchingLive(true);
    try {
      const res = await apiClient.get('/students/search', {
        params: { q, session_id: currentSessionId, page: 1, limit: 8 },
      });
      if (seq !== scanSeqRef.current) return null; // superseded
      const rows: RosterStudent[] = res.data.status === 'success' ? (res.data.data || []).map(mapRosterRow) : [];
      remember(rows);
      return rows;
    } catch {
      return seq === scanSeqRef.current ? [] : null;
    } finally {
      if (seq === scanSeqRef.current) setSearchingLive(false);
    }
  };

  const handleScanTextChange = (value: string) => {
    setScanText(value);
    if (autoMark) return; // auto-mark: no search, Enter marks by SP ID
    if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);
    const q = value.trim();
    if (q.length < 2) {
      scanSeqRef.current++;
      setScanResults([]);
      setSearchingLive(false);
      return;
    }
    scanDebounceRef.current = setTimeout(async () => {
      const rows = await searchStudents(q);
      if (rows) setScanResults(rows);
    }, 250);
  };

  const resetScanBox = () => {
    if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);
    scanSeqRef.current++;
    setScanText('');
    setScanResults([]);
    setSearchingLive(false);
    scanInput()?.focus();
  };

  const recordFromScan = async (student: RosterStudent, direction: ScanMode) => {
    setScanning(true);
    const result = await recordOne(student, direction);
    setScanning(false);

    if (!result.ok) {
      toast.current?.show({ severity: 'error', summary: 'Not recorded', detail: result.message, life: 4500 });
      scanInput()?.select();
      return;
    }

    resetScanBox();
    const m = result.movement!;
    if (rowRefs.current[m.student_id]) {
      // The roster shows them — the row flashing is the confirmation.
      scrollToRow(m.student_id);
    } else {
      // Not in the roster on screen (other class / filtered out) — say it.
      toast.current?.show({
        severity: 'success',
        summary: `${m.student_name} · ${m.direction}`,
        detail: `Marked ${m.direction === 'IN' ? 'in' : 'out'} at ${formatTime(m.timestamp)} (${TYPE_LABEL[m.type] || m.type}).`,
        life: 2500,
      });
    }
    syncIfOffline();
  };

  // ── Auto-mark: one request per scan, last 10 kept to stop repeats ─────────

  const todaysRecent = (): RecentScan[] => (recentDay.current === toDateParam(new Date()) ? recent : []);

  const pushRecent = (entry: RecentScan) => {
    const today = toDateParam(new Date());
    const base = recentDay.current === today ? recentRef.current : []; // new day, fresh list
    recentDay.current = today;
    // Newest on top; the oldest drops off once there are more than 10.
    const next = [entry, ...base.filter((r) => r.sp !== entry.sp)].slice(0, RECENT_LIMIT);
    recentRef.current = next;
    setRecent(next);
  };

  const flashRecent = (sp: string) => {
    setRecentFlash(sp);
    setTimeout(() => setRecentFlash((cur) => (cur === sp ? null : cur)), 1200);
  };

  // Enter in auto-mark: clear the box at once and queue the scan, so the guard
  // can scan the next card straight away — nothing waits for the network and
  // nothing typed meanwhile is lost. The queue runs one request at a time,
  // in scan order.
  const autoMarkScan = () => {
    const sp = scanText.trim().toUpperCase();
    if (!sp) return;
    setScanText('');
    scanQueue.current.push({ sp, mode: scanMode, gate });
    setQueued(scanQueue.current.length);
    runScanQueue();
  };

  const runScanQueue = async () => {
    if (queueRunning.current) return;
    queueRunning.current = true;
    setScanning(true);
    try {
      while (scanQueue.current.length) {
        const item = scanQueue.current.shift()!;
        setQueued(scanQueue.current.length);
        await markBySp(item);
      }
    } finally {
      queueRunning.current = false;
      setScanning(false);
      setQueued(0);
    }
  };

  const markBySp = async ({ sp, mode, gate: atGate }: { sp: string; mode: ScanMode; gate: string }) => {
    // Checked when its turn comes, so a card scanned twice in a row is
    // stopped by the first scan's result — without a second request.
    const today = toDateParam(new Date());
    const hit = recentDay.current === today ? recentRef.current.find((r) => r.sp === sp) : undefined;
    const reason = hit ? repeatScanReason(hit, mode) : null;
    if (reason) {
      toast.current?.show({ severity: 'warn', summary: 'Already marked', detail: reason, life: 3000 });
      flashRecent(sp);
      return;
    }

    try {
      const res = await apiClient.post('api/movements', {
        sp_student_id: sp,
        direction: mode,
        gate: atGate,
        method: 'qr',
      });
      const d = res.data;
      if (d.status === 'success') {
        const movement: RecordedMovement = {
          movement_id: d.movement_id,
          student_id: d.student_id,
          student_name: d.student_name,
          direction: d.direction,
          type: d.type,
          timestamp: d.timestamp || new Date().toISOString(),
        };
        applyLocal([movement]);
        pushRecent({ sp: d.sp_student_id || sp, name: d.student_name, direction: d.direction, at: movement.timestamp });
        if (movement.type === 'unauthorized') {
          toast.current?.show({
            severity: 'warn',
            summary: 'Left without a pass',
            detail: `${movement.student_name} went out during school hours without an approved gate pass.`,
            life: 7000,
          });
        }
        syncIfOffline();
      } else {
        // "Already IN / already left": remember it so the next repeat scan
        // is stopped here.
        if (d.state) {
          pushRecent({
            sp: d.state.sp_student_id || sp,
            name: d.state.student_name,
            direction: d.state.last_direction,
            at: d.state.last_at,
            repeat: true,
          });
        }
        toast.current?.show({ severity: 'error', summary: `Not recorded · ${sp}`, detail: d.message, life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: `Not recorded · ${sp}`,
        detail: err.response?.data?.message || 'Could not record this movement.',
        life: 4000,
      });
    }
  };

  // Enter: a scanner types the full SP ID then Enter, so search immediately
  // (don't wait for the debounce) and record the exact match.
  const handleScanEnter = async () => {
    if (autoMark) return autoMarkScan();
    const q = scanText.trim();
    if (q.length < 2 || scanning) return;
    if (!currentSessionId) {
      toast.current?.show({ severity: 'warn', summary: 'Not ready', detail: 'School session not loaded yet — try again in a moment.', life: 3500 });
      return;
    }
    if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);

    const rows = await searchStudents(q);
    if (!rows) return;
    const exact = rows.find((r) => r.spStudentId.toLowerCase() === q.toLowerCase());
    const target = exact || (rows.length === 1 ? rows[0] : null);

    if (target) {
      await recordFromScan(target, scanMode);
    } else if (rows.length > 1) {
      setScanResults(rows); // let the guard tap the right one
    } else {
      toast.current?.show({ severity: 'warn', summary: 'No match', detail: `No student matched "${q}".`, life: 3000 });
      setScanResults([]);
      scanInput()?.select(); // next scan overwrites the bad text
    }
  };

  const handleRowMark = async (s: RosterStudent, direction: Direction) => {
    setBusyIds((prev) => new Set(prev).add(s.studentId));
    const result = await recordOne(s, direction);
    if (!result.ok) {
      toast.current?.show({ severity: 'error', summary: 'Not recorded', detail: result.message, life: 4000 });
    }
    setBusyIds((prev) => {
      const next = new Set(prev);
      next.delete(s.studentId);
      return next;
    });
    syncIfOffline();
  };

  const handleBulk = async (direction: Direction) => {
    // Only send students the action makes sense for; the backend would skip
    // the rest anyway, but this keeps the result message meaningful.
    const ids = Array.from(selected).filter((id) => nextActionOf(id) === direction);
    if (ids.length === 0) return;

    setBulkBusy(direction);
    let recordedCount = 0;
    const skipped: { reason: string }[] = [];
    let failed = '';
    try {
      for (const part of chunk(ids, BULK_CHUNK)) {
        const res = await apiClient.post('api/movements/bulk', {
          student_ids: part,
          direction,
          gate,
          method: 'manual',
        });
        if (res.data.status !== 'success') {
          failed = res.data.message || 'The students could not be marked.';
          break;
        }
        const recorded: RecordedMovement[] = res.data.recorded || [];
        applyLocal(recorded); // rows update batch by batch
        recordedCount += recorded.length;
        skipped.push(...(res.data.skipped || []));
      }
    } catch (err: any) {
      failed = err.response?.data?.message || 'Something went wrong while marking.';
    } finally {
      setBulkBusy(null);
      setSelected(new Set());
      syncIfOffline();
    }

    if (failed) {
      toast.current?.show({
        severity: 'error',
        summary: recordedCount ? `${recordedCount} marked, then stopped` : 'Failed',
        detail: failed,
        life: 6000,
      });
    } else if (skipped.length) {
      toast.current?.show({
        severity: 'warn',
        summary: `${recordedCount} marked ${direction} · ${skipped.length} skipped`,
        detail: skipped
          .slice(0, 3)
          .map((s) => s.reason)
          .join(' ') + (skipped.length > 3 ? ' …' : ''),
        life: 6000,
      });
    } else {
      toast.current?.show({
        severity: 'success',
        summary: `${recordedCount} marked ${direction}`,
        detail: direction === 'IN' ? 'Late arrivals were flagged automatically.' : 'Exits during school hours without a pass were flagged.',
        life: 3500,
      });
    }
  };

  // ── Selection ──────────────────────────────────────────────────────────────

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected = filteredRoster.length > 0 && filteredRoster.every((s) => selected.has(s.studentId));

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filteredRoster.forEach((s) => next.delete(s.studentId));
      else filteredRoster.forEach((s) => next.add(s.studentId));
      return next;
    });

  const selectedCanIn = Array.from(selected).filter((id) => nextActionOf(id) === 'IN').length;
  const selectedCanOut = Array.from(selected).filter((id) => nextActionOf(id) === 'OUT').length;

  // ── Render ─────────────────────────────────────────────────────────────────

  // Tiles with `logFilter` open Today's log filtered to that group.
  // Counted from the live rows, so socket updates move the numbers instantly.
  const liveTotals = useMemo(() => {
    let inside = 0;
    let late = 0;
    let gone = 0;
    Object.values(live).forEach((r) => {
      if (r.status === 'in_campus') inside++;
      else if (r.status === 'left_for_day' || r.status === 'departed_early') gone++;
      if (r.arrived_late) late++;
    });
    return { inside, late, gone };
  }, [live]);

  const stats: { label: string; value: number; color: string; icon: string; logFilter?: LogTypeFilter }[] = [
    { label: 'Inside now', value: liveTotals.inside, color: '#15803d', icon: 'pi pi-check-circle' },
    { label: 'Late today', value: liveTotals.late, color: '#b45309', icon: 'pi pi-clock', logFilter: 'late' },
    { label: 'Gone for the day', value: liveTotals.gone, color: '#475569', icon: 'pi pi-sign-out' },
  ];

  const openLogFiltered = (f: LogTypeFilter) => {
    setLogTypeFilter(f);
    setTab('log');
  };

  const pickClass = (id: string) => {
    setClassId(id);
    setSection(null);
    setRosterFilter('all');
    setRosterSearch('');
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <style>{`
          .ge-card { background:white; border-radius:18px; padding:18px 20px; box-shadow:0 2px 10px rgba(30,64,175,.07); border:1px solid rgba(59,130,246,.12); }
          .ge-top { display:grid; grid-template-columns:auto minmax(0,1fr); gap:12px; align-items:stretch; margin-bottom:16px; }
          @media (max-width: 900px) { .ge-top { grid-template-columns:1fr; } .ge-top .ge-tabs { justify-self:start; } }
          .ge-stats { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:12px; }
          @media (max-width: 640px) { .ge-stats { grid-template-columns:1fr; } }
          .ge-stat { background:white; border-radius:14px; padding:14px 16px; border:1px solid #e5e7eb; display:flex; align-items:center; gap:12px; }
          .ge-stat.clickable { cursor:pointer; transition:border-color .15s, box-shadow .15s, transform .15s; }
          .ge-stat.clickable:hover { border-color:var(--tile-color); box-shadow:0 4px 14px rgba(15,23,42,.08); transform:translateY(-1px); }
          .ge-stat.selected { border-color:var(--tile-color); box-shadow:inset 0 0 0 1px var(--tile-color); }
          .ge-stat-go { color:#cbd5e1; transition:color .15s; }
          .ge-stat.clickable:hover .ge-stat-go { color:var(--tile-color); }
          /* Same card as the stat tiles, so the row reads as one strip. */
          .ge-panel { background:white; border-radius:14px; border:1px solid #e5e7eb; }
          .ge-tabs { display:flex; align-items:center; gap:6px; padding:8px; }
          .ge-live { display:inline-flex; align-items:center; gap:6px; margin:0 8px 0 4px; font-size:11.5px; font-weight:700; color:#94a3b8; white-space:nowrap; }
          .ge-live.on { color:#15803d; }
          .ge-live-dot { width:8px; height:8px; border-radius:50%; background:#cbd5e1; }
          .ge-live.on .ge-live-dot { background:#22c55e; box-shadow:0 0 0 0 rgba(34,197,94,.5); animation:gePulse 2s infinite; }
          @keyframes gePulse { 0% { box-shadow:0 0 0 0 rgba(34,197,94,.5); } 70% { box-shadow:0 0 0 7px rgba(34,197,94,0); } 100% { box-shadow:0 0 0 0 rgba(34,197,94,0); } }
          .ge-tab { border:none; background:transparent; padding:0 20px; height:100%; min-height:48px; border-radius:10px; font-size:13.5px; font-weight:700; color:#64748b; cursor:pointer; display:inline-flex; align-items:center; gap:8px; white-space:nowrap; transition:background .15s, color .15s; }
          .ge-tab:hover:not(.active) { background:#f1f5f9; color:#1e40af; }
          .ge-tab.active { background:linear-gradient(135deg,#1e40af,#3b82f6); color:white; box-shadow:0 2px 8px rgba(59,130,246,.3); }
          .ge-grid { display:grid; grid-template-columns:minmax(0, 360px) minmax(0, 1fr); gap:16px; align-items:start; }
          @media (max-width: 1000px) { .ge-grid { grid-template-columns:1fr; } }
          .ge-sticky { position:sticky; top:16px; }
          @media (max-width: 1000px) { .ge-sticky { position:static; } }
          .ge-seg { display:inline-flex; gap:4px; background:#f1f5f9; border-radius:12px; padding:4px; }
          .ge-seg-btn { border:none; background:transparent; padding:7px 14px; border-radius:9px; font-size:13px; font-weight:600; color:#64748b; cursor:pointer; transition:all .15s; display:inline-flex; align-items:center; gap:6px; }
          .ge-seg-btn.active { background:white; color:#1e40af; box-shadow:0 1px 4px rgba(15,23,42,.1); }
          .ge-scan { position:relative; }
          .ge-scan .global-input-field { font-size:15px !important; font-weight:600; padding-right:76px !important; }
          .ge-scan-hint { position:absolute; right:14px; top:50%; transform:translateY(-50%); font-size:11px; font-weight:700; color:#94a3b8; display:flex; align-items:center; pointer-events:none; }
          .ge-result { display:flex; align-items:center; gap:10px; padding:8px; border-radius:10px; background:white; margin-bottom:4px; }
          @keyframes geRecentIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:none; } }
          @keyframes geRecentFlash { 0%,100% { background:#f8fafc; } 50% { background:rgba(245,158,11,.25); } }
          .ge-recent { display:flex; align-items:center; gap:10px; padding:6px 10px; border-radius:10px; background:#f8fafc; border:1px solid #f1f5f9; animation:geRecentIn .25s ease-out; }
          .ge-recent.flash { animation:geRecentFlash .4s ease-in-out 3; }
          .ge-recent-dir { font-size:10.5px; font-weight:800; padding:3px 7px; border-radius:6px; letter-spacing:.5px; min-width:34px; text-align:center; }
          .ge-result:last-child { margin-bottom:0; }
          .ge-class-chip { padding:6px 14px; border-radius:8px; border:1.5px solid #e5e7eb; background:white; color:#1f2937; font-size:12px; font-weight:600; cursor:pointer; transition:all .15s; }
          .ge-class-chip:hover { transform:translateY(-1px); }
          .ge-class-chip.active { border-color:#3b82f6; background:linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%); color:white; box-shadow:0 2px 8px rgba(59,130,246,.25); }
          .ge-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border-radius:20px; font-size:12px; font-weight:600; cursor:pointer; border:1px solid #e5e7eb; background:white; color:#4b5563; }
          .ge-row { display:grid; grid-template-columns:36px 74px minmax(0,1fr) 230px 136px; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #f1f5f9; transition:background .4s; }
          .ge-row:hover { background:rgba(65,105,225,.03); }
          .ge-row.selected { background:rgba(239,246,255,.75); }
          .ge-row.flash-IN { animation: geFlashIn ${FLASH_MS}ms ease-out; }
          .ge-row.flash-OUT { animation: geFlashOut ${FLASH_MS}ms ease-out; }
          @keyframes geFlashIn { 0%,35% { background:rgba(22,163,74,.20); box-shadow:inset 4px 0 0 #16a34a; } 100% { background:transparent; box-shadow:inset 4px 0 0 transparent; } }
          @keyframes geFlashOut { 0%,35% { background:rgba(51,65,85,.14); box-shadow:inset 4px 0 0 #334155; } 100% { background:transparent; box-shadow:inset 4px 0 0 transparent; } }
          .ge-row.head { background:#f9fafb; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.8px; color:#6b7280; padding-top:12px; padding-bottom:12px; position:sticky; top:0; z-index:1; }
          @media (max-width: 760px) { .ge-row { grid-template-columns:32px minmax(0,1fr) 112px; } .ge-row .ge-hide-sm { display:none; } }
          .ge-time { display:inline-flex; align-items:center; gap:5px; font-size:11.5px; font-weight:600; color:#475569; white-space:nowrap; }
          .ge-time-tag { font-size:9.5px; font-weight:800; letter-spacing:.4px; padding:2px 5px; border-radius:4px; }
          .ge-action { border:none; border-radius:9px; padding:8px 14px; font-size:12.5px; font-weight:700; color:white; cursor:pointer; display:inline-flex; align-items:center; gap:6px; white-space:nowrap; min-width:112px; justify-content:center; transition:filter .12s, opacity .12s; }
          .ge-action:hover:not(:disabled) { filter:brightness(1.08); }
          .ge-action:disabled { opacity:.5; cursor:not-allowed; }
          .ge-done { font-size:12px; font-weight:600; color:#94a3b8; display:inline-flex; align-items:center; gap:6px; min-width:112px; justify-content:center; white-space:nowrap; }
          .ge-bulk { border:none; border-radius:10px; padding:10px 16px; font-size:13.5px; font-weight:700; color:white; cursor:pointer; display:inline-flex; align-items:center; gap:8px; }
          .ge-bulk:disabled { opacity:.45; cursor:not-allowed; }
          .ge-check { width:18px; height:18px; accent-color:#2563eb; cursor:pointer; }
          .ge-list { max-height: calc(100vh - 260px); min-height: 360px; overflow-y:auto; }
        `}</style>

        <GlobalHeaderTitle
          title="Gate Entry"
          subtitle="Mark students in and out of school — no forms needed"
          icon="pi pi-sign-in"
          buttons={[
            {
              label: 'Refresh',
              icon: 'pi pi-refresh',
              onClick: () => {
                fetchLive();
                fetchRoster();
              },
              outlined: true,
              disabled: liveLoading,
              style: { borderWidth: '2px' },
            },
          ]}
        />

        {/* ── Tabs (left) and live counts (right) on one row ── */}
        <div className="ge-top">
          <div className="ge-tabs ge-panel">
            <button className={`ge-tab ${tab === 'mark' ? 'active' : ''}`} onClick={() => setTab('mark')}>
              <i className="pi pi-sign-in" style={{ fontSize: 13 }} />
              Mark entries
            </button>
            <button
              className={`ge-tab ${tab === 'log' ? 'active' : ''}`}
              onClick={() => {
                if (tab !== 'log') setLogTypeFilter(null);
                setTab('log');
              }}
            >
              <i className="pi pi-list" style={{ fontSize: 13 }} />
              Today's log
            </button>
            {socketStatus !== 'off' && (
              <span
                className={`ge-live ${socketLive ? 'on' : ''}`}
                title={
                  socketLive
                    ? 'Live — marks from every gate appear instantly'
                    : 'Live updates paused — reconnecting; refreshing every 30 s meanwhile'
                }
              >
                <span className="ge-live-dot" />
                {socketLive ? 'Live' : socketStatus === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
              </span>
            )}
          </div>

          <div className="ge-stats">
            {stats.map((s) => (
              <div
                key={s.label}
                className={`ge-stat ${s.logFilter ? 'clickable' : ''} ${s.logFilter && tab === 'log' && logTypeFilter === s.logFilter ? 'selected' : ''}`}
                style={{ ['--tile-color' as any]: s.color }}
                onClick={s.logFilter ? () => openLogFiltered(s.logFilter!) : undefined}
                role={s.logFilter ? 'button' : undefined}
                title={s.logFilter ? `Show ${s.label.toLowerCase()} in Today's log` : undefined}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `${s.color}14`,
                    color: s.color,
                    flexShrink: 0,
                  }}
                >
                  <i className={s.icon} style={{ fontSize: 15 }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
                </div>
                {s.logFilter && <i className="pi pi-angle-right ge-stat-go" style={{ marginLeft: 'auto', fontSize: 14 }} />}
              </div>
            ))}
          </div>
        </div>

        {tab === 'log' && (
          <TodayGateLog
            classOptions={classOptions}
            refreshKey={logRefreshKey}
            autoRefresh={!socketLive}
            typeFilter={logTypeFilter}
            onTypeFilterChange={setLogTypeFilter}
          />
        )}

        {/* Kept mounted (just hidden) on the log tab so the scan box and the
            loaded roster survive switching back and forth. */}
        <div className="ge-grid" style={tab === 'mark' ? undefined : { display: 'none' }}>
          {/* ── Quick scan ── */}
          <div className="ge-card ge-sticky" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a8a' }}>
                <i className="pi pi-qrcode" style={{ marginRight: 8 }} />
                Quick scan
              </div>
              <div className="ge-seg">
                {GATES.map((g) => (
                  <button
                    key={g.value}
                    className={`ge-seg-btn ${gate === g.value ? 'active' : ''}`}
                    onClick={() => {
                      setGate(g.value);
                      try {
                        localStorage.setItem(GATE_STORAGE_KEY, g.value);
                      } catch {
                        /* per-desk preference only */
                      }
                    }}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="ge-seg" style={{ alignSelf: 'flex-start' }}>
              {(['AUTO', 'IN', 'OUT'] as ScanMode[]).map((m) => (
                <button
                  key={m}
                  className={`ge-seg-btn ${scanMode === m ? 'active' : ''}`}
                  onClick={() => {
                    setScanMode(m);
                    scanInput()?.focus();
                  }}
                  style={scanMode === m && m !== 'AUTO' ? { color: m === 'IN' ? IN_COLOR : OUT_COLOR } : undefined}
                >
                  {m === 'AUTO' && <i className="pi pi-sync" style={{ fontSize: 11 }} />}
                  {m === 'IN' && <i className="pi pi-sign-in" style={{ fontSize: 11 }} />}
                  {m === 'OUT' && <i className="pi pi-sign-out" style={{ fontSize: 11 }} />}
                  {m === 'AUTO' ? 'Auto' : m === 'IN' ? 'Entry' : 'Exit'}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: -6, fontWeight: 500 }}>
              {scanMode === 'AUTO'
                ? 'Enter flips the student: not in yet → IN, inside → OUT.'
                : scanMode === 'IN'
                  ? 'Enter marks the student as entering.'
                  : 'Enter marks the student as leaving.'}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>
                  <i className="pi pi-bolt" style={{ fontSize: 12, marginRight: 6 }} />
                  Auto-mark on scan
                </div>
                <div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 500, marginTop: 2 }}>
                  {autoMark ? 'Scan marks instantly by SP ID — no search' : 'Off: search, then tap or press Enter'}
                </div>
              </div>
              <div className="ge-seg">
                {[true, false].map((on) => (
                  <button
                    key={String(on)}
                    className={`ge-seg-btn ${autoMark === on ? 'active' : ''}`}
                    style={autoMark === on && on ? { color: IN_COLOR } : undefined}
                    onClick={() => {
                      setAutoMark(on);
                      resetScanBox();
                      try {
                        localStorage.setItem(AUTO_MARK_STORAGE_KEY, on ? '1' : '0');
                      } catch {
                        /* per-desk preference only */
                      }
                    }}
                  >
                    {on ? 'On' : 'Off'}
                  </button>
                ))}
              </div>
            </div>

            <div
              ref={scanBoxRef}
              className="ge-scan"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleScanEnter();
                if (e.key === 'Escape') resetScanBox();
              }}
            >
              <GlobalInputField
                value={scanText}
                onChange={(e) => handleScanTextChange(e.target.value)}
                placeholder={autoMark ? 'Scan ID card — marks instantly' : 'Scan ID card, or type a name / SP ID'}
                icon="pi pi-id-card"
                autoComplete="off"
              />
              <span className="ge-scan-hint">
                {queued > 0 ? (
                  `${queued} QUEUED`
                ) : scanning || searchingLive ? (
                  <ProgressSpinner style={{ width: 18, height: 18 }} strokeWidth="5" animationDuration=".8s" />
                ) : (
                  'ENTER ↵'
                )}
              </span>
            </div>

            {scanResults.length > 0 && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 6, background: '#f8fafc' }}>
                {scanResults.map((s) => {
                  const st = statusOf(s.studentId);
                  const action = nextActionFor(st);
                  return (
                    <div key={s.studentId} className="ge-result">
                      <ProfilePictureHover src={s.photoUrl} name={s.name} size={34} borderRadius={9} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#1f2937',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {s.name}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                          {[s.classLabel, s.spStudentId].filter(Boolean).join(' · ')}
                        </div>
                        <div style={{ marginTop: 3 }}>
                          <StatusPill status={st} overdue={live[s.studentId]?.overdue} />
                        </div>
                      </div>
                      <ActionButton
                        action={action}
                        status={st}
                        disabled={scanning}
                        onClick={(dir) => recordFromScan(s, dir)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {!autoMark && scanText.trim().length >= 2 && !searchingLive && scanResults.length === 0 && (
              <div style={{ fontSize: 12.5, color: '#94a3b8', fontWeight: 500, marginTop: -4 }}>
                No match yet — keep typing, or press Enter.
              </div>
            )}

            <div
              style={{
                fontSize: 12,
                color: '#94a3b8',
                lineHeight: 1.5,
                borderTop: '1px solid #f1f5f9',
                paddingTop: 12,
                fontWeight: 500,
              }}
            >
              Marked students update in the roster right away. Esc clears the box.
            </div>

            {todaysRecent().length > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                    Recent scans
                  </span>
                  <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                    last {RECENT_LIMIT} · repeats stopped here
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {todaysRecent().map((r) => (
                    <div
                      key={`${r.sp}-${r.at}`}
                      className={`ge-recent ${recentFlash === r.sp ? 'flash' : ''}`}
                    >
                      <span
                        className="ge-recent-dir"
                        style={{
                          color: r.direction === 'IN' ? IN_COLOR : OUT_COLOR,
                          background: r.direction === 'IN' ? 'rgba(22,163,74,.1)' : 'rgba(51,65,85,.08)',
                        }}
                      >
                        {r.direction}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.name}
                        </div>
                        <div style={{ fontSize: 10.5, color: '#94a3b8', fontFamily: 'monospace' }}>{r.sp}</div>
                      </div>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>
                        {r.repeat ? 'already · ' : ''}
                        {formatTime(r.at)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Roster ── */}
          <div className="ge-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a8a', marginBottom: 12 }}>
                <i className="pi pi-users" style={{ marginRight: 8 }} />
                {isAllClasses ? 'Marked today' : `Class ${selectedClass?.label ?? ''}${section ? ` · Section ${section}` : ''}`}
                <span style={{ fontSize: 12.5, fontWeight: 500, color: '#94a3b8', marginLeft: 10 }}>
                  {isAllClasses
                    ? 'new arrivals appear on top · pick a class to see who hasn\'t arrived'
                    : 'whole class · tick students to mark in bulk'}
                </span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button className={`ge-class-chip ${isAllClasses ? 'active' : ''}`} onClick={() => pickClass(ALL_CLASSES)}>
                  <i className="pi pi-bolt" style={{ fontSize: 11, marginRight: 6 }} />
                  Marked today
                </button>
                {classesLoading ? (
                  <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500, alignSelf: 'center' }}>Loading classes…</span>
                ) : (
                  classOptions.map((cls) => (
                    <button
                      key={cls.id}
                      className={`ge-class-chip ${classId === cls.id ? 'active' : ''}`}
                      onClick={() => pickClass(classId === cls.id ? ALL_CLASSES : cls.id)}
                    >
                      {cls.label}
                    </button>
                  ))
                )}
              </div>

              {sections.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 10 }}>
                  <button className={`ge-class-chip ${section === null ? 'active' : ''}`} onClick={() => setSection(null)}>
                    All sections
                  </button>
                  {sections.map((sec) => (
                    <button
                      key={sec}
                      className={`ge-class-chip ${section === sec ? 'active' : ''}`}
                      onClick={() => setSection(section === sec ? null : sec)}
                    >
                      Section {sec}
                    </button>
                  ))}
                </div>
              )}

              {currentSessionId && baseList.length > 0 && (
                <>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 14 }}>
                    {(
                      [
                        { v: 'all', label: 'All', n: baseList.length },
                        ...(isAllClasses ? [] : [{ v: 'not_arrived', label: 'Not arrived', n: rosterCounts.notArrived }]),
                        { v: 'came_in', label: 'Came in', n: rosterCounts.cameIn },
                        { v: 'inside', label: 'Still inside', n: rosterCounts.inside },
                        { v: 'out', label: 'Left', n: rosterCounts.out },
                      ] as { v: RosterFilter; label: string; n: number }[]
                    ).map((f) => (
                      <button
                        key={f.v}
                        className="ge-chip"
                        onClick={() => setRosterFilter(f.v)}
                        style={rosterFilter === f.v ? { background: '#1e40af', borderColor: '#1e40af', color: 'white' } : undefined}
                      >
                        {f.label}
                        <span style={{ fontWeight: 800 }}>{f.n}</span>
                      </button>
                    ))}
                    <div style={{ marginLeft: 'auto', minWidth: 220, flex: '0 1 300px' }}>
                      <GlobalInputField
                        value={rosterSearch}
                        onChange={(e) => setRosterSearch(e.target.value)}
                        placeholder="Find in this list — name, roll, SP ID"
                        icon="pi pi-search"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {selected.size > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        flexWrap: 'wrap',
                        marginTop: 14,
                        padding: '10px 12px',
                        borderRadius: 12,
                        background: 'rgba(239,246,255,.9)',
                        border: '1px solid rgba(59,130,246,.25)',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', flex: 1, minWidth: 140 }}>
                        {selected.size} selected
                        <button
                          onClick={() => setSelected(new Set())}
                          style={{
                            marginLeft: 10,
                            background: 'none',
                            border: 'none',
                            color: '#64748b',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Clear
                        </button>
                      </span>
                      <button
                        className="ge-bulk"
                        style={{ background: IN_COLOR }}
                        disabled={!selectedCanIn || !!bulkBusy}
                        onClick={() => handleBulk('IN')}
                      >
                        <i className={bulkBusy === 'IN' ? 'pi pi-spin pi-spinner' : 'pi pi-sign-in'} />
                        Mark IN{selectedCanIn ? ` (${selectedCanIn})` : ''}
                      </button>
                      <button
                        className="ge-bulk"
                        style={{ background: OUT_COLOR }}
                        disabled={!selectedCanOut || !!bulkBusy}
                        onClick={() => handleBulk('OUT')}
                      >
                        <i className={bulkBusy === 'OUT' ? 'pi pi-spin pi-spinner' : 'pi pi-sign-out'} />
                        Mark OUT{selectedCanOut ? ` (${selectedCanOut})` : ''}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {!currentSessionId ? (
              <EmptyRoster
                icon="pi pi-exclamation-circle"
                title="No active session"
                message="Could not determine the current session for this school. Please refresh and try again."
              />
            ) : isAllClasses && markedToday.length === 0 ? (
              <EmptyRoster
                icon="pi pi-bolt"
                title="Nobody marked yet today"
                message="Scan an ID card or type a name on the left — each student appears here the moment they're marked. Pick a class above to mark a whole class at once."
              />
            ) : rosterLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 60 }}>
                <ProgressSpinner style={{ width: 36, height: 36 }} strokeWidth="4" animationDuration=".8s" />
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                  Loading class…
                </span>
              </div>
            ) : !isAllClasses && roster.length === 0 ? (
              <EmptyRoster icon="pi pi-inbox" title="No students" message="No active students here in the current session." />
            ) : (
              <div className="ge-list">
                <div className="ge-row head">
                  <input type="checkbox" className="ge-check" checked={allVisibleSelected} onChange={toggleAllVisible} />
                  <span className="ge-hide-sm">{isAllClasses ? 'Class' : 'Roll'}</span>
                  <span>Student</span>
                  <span className="ge-hide-sm">Today</span>
                  <span style={{ textAlign: 'center' }}>Action</span>
                </div>
                {filteredRoster.length === 0 ? (
                  <div style={{ padding: 30, textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
                    {rosterSearch ? `Nobody here matches "${rosterSearch}".` : 'Nobody in this group right now.'}
                  </div>
                ) : (
                  filteredRoster.map((s) => {
                    const st = statusOf(s.studentId);
                    const liveRow = live[s.studentId];
                    const action = nextActionFor(st);
                    const busy = busyIds.has(s.studentId) || !!bulkBusy;
                    const flashDir = flash[s.studentId];
                    return (
                      <div
                        key={s.studentId}
                        ref={(el) => {
                          rowRefs.current[s.studentId] = el;
                        }}
                        className={`ge-row ${selected.has(s.studentId) ? 'selected' : ''} ${flashDir ? `flash-${flashDir}` : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="ge-check"
                          checked={selected.has(s.studentId)}
                          onChange={() => toggleOne(s.studentId)}
                        />
                        <span className="ge-hide-sm" style={{ fontSize: 12.5, fontWeight: 700, color: '#64748b' }}>
                          {isAllClasses ? (
                            <>
                              {s.classLabel || '—'}
                              {s.rollNo !== null && (
                                <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>
                                  Roll {s.rollNo}
                                </span>
                              )}
                            </>
                          ) : (
                            s.rollNo ?? '—'
                          )}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                          <ProfilePictureHover src={s.photoUrl} name={s.name} size={34} borderRadius={9} />
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13.5,
                                fontWeight: 600,
                                color: '#1f2937',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {s.name}
                            </div>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{s.spStudentId}</div>
                          </div>
                        </div>
                        <div
                          className="ge-hide-sm"
                          style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}
                        >
                          <StatusPill status={st} overdue={liveRow?.overdue} />
                          {liveRow && <InOutTimes row={liveRow} />}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <ActionButton
                            action={action}
                            status={st}
                            disabled={busy}
                            busy={busyIds.has(s.studentId)}
                            onClick={(dir) => handleRowMark(s, dir)}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ── Small pieces ────────────────────────────────────────────────────────────

const StatusPill: React.FC<{ status: LiveStatus | 'not_arrived'; overdue?: boolean }> = ({ status, overdue }) => {
  const d = STATUS_DISPLAY[status];
  const color = overdue ? '#b91c1c' : d.color;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 9px',
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 700,
        color,
        background: overdue ? 'rgba(239,68,68,.10)' : d.bg,
        whiteSpace: 'nowrap',
      }}
    >
      <i className={overdue ? 'pi pi-exclamation-triangle' : d.icon} style={{ fontSize: 10 }} />
      {overdue ? 'Overdue' : d.label}
    </span>
  );
};

// The day's entry and exit times side by side: "IN 8:22 am · Late  OUT 4:51 pm".
// OUT is shown while the student is out (left, short leave, …); once they're
// back inside after a short leave only the entry time stays.
const InOutTimes: React.FC<{ row: LiveRow }> = ({ row }) => {
  const showOut = row.status !== 'in_campus' && !!row.last_out_at;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {row.first_in_at && (
        <span className="ge-time">
          <span className="ge-time-tag" style={{ background: 'rgba(22,163,74,.12)', color: IN_COLOR }}>IN</span>
          {formatTime(row.first_in_at)}
          {row.arrived_late && <span style={{ color: '#b45309', fontWeight: 700 }}>· Late</span>}
        </span>
      )}
      {showOut && (
        <span className="ge-time">
          <span className="ge-time-tag" style={{ background: 'rgba(51,65,85,.12)', color: OUT_COLOR }}>OUT</span>
          {formatTime(row.last_out_at!)}
        </span>
      )}
    </div>
  );
};

// One button with the student's next step — never both IN and OUT at once.
const ActionButton: React.FC<{
  action: Direction | null;
  status: LiveStatus | 'not_arrived';
  disabled?: boolean;
  busy?: boolean;
  onClick: (dir: Direction) => void;
}> = ({ action, status, disabled, busy, onClick }) => {
  if (!action) {
    return (
      <span className="ge-done" title="Final exit recorded — nothing more today">
        <i className="pi pi-lock" style={{ fontSize: 10 }} />
        Done for the day
      </span>
    );
  }
  const isIn = action === 'IN';
  return (
    <button
      className="ge-action"
      style={{ background: isIn ? IN_COLOR : OUT_COLOR }}
      disabled={disabled}
      onClick={() => onClick(action)}
    >
      <i className={busy ? 'pi pi-spin pi-spinner' : isIn ? 'pi pi-sign-in' : 'pi pi-sign-out'} style={{ fontSize: 11 }} />
      {isIn ? (status === 'out_on_pass' ? 'Mark back IN' : 'Mark IN') : 'Mark OUT'}
    </button>
  );
};

const EmptyRoster: React.FC<{ icon: string; title: string; message: string }> = ({ icon, title, message }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 20px', textAlign: 'center' }}>
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 14,
        background: 'linear-gradient(135deg,#1e40af,#3b82f6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
      }}
    >
      <i className={icon} style={{ fontSize: 22, color: 'white' }} />
    </div>
    <div style={{ fontSize: 17, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 13.5, color: '#64748b', maxWidth: 340, lineHeight: 1.6 }}>{message}</div>
  </div>
);


export default GateEntryDashboard;